import { useEffect, useState } from 'preact/hooks';
import { Ban, Check, Crosshair, Lock, Palette, Timer } from 'lucide-preact';
import {
  CAMOS,
  GADGET_BY_ID,
  GADGETS,
  masteryKillsFor,
  masteryTierFor,
  MODE_INFO,
  nextMasteryTier,
  PET_BY_ID,
  PETS,
  SHOP_WEAPONS,
  SCOPE_BY_ID as SCOPE_BY_ID_LOCAL,
  scopesFor,
  sightFor,
  zoomLabel,
  TRACERS,
  WEAPON_BY_ID,
  type MasteryLevel,
} from '@vm/content';
import { SLOT_NAMES, type GadgetDef, type PetDef, type WeaponDef } from '@vm/shared';
import { WeaponPreview } from '../three/WeaponPreview';
import { buyItem, equipGadget, equipPet, equipWeapon, setCamo, setScope, setTracer } from '../state/actions';
import { itemInfo, liveWeapons, ownsItem, PET_ROLE, petFacts, weaponOff, weaponStats } from '../state/content';
import { go, replace, route } from '../state/nav';
import { cosmetics, inventory, isOffline, level, loadout, me, toast, wallet } from '../state/store';
import { fmtNum, hex } from '../ui/format';
import { useCompact } from '../ui/hooks';
import { Icon } from '../ui/icons';
import { AsyncButton, Bar, confirmDialog, Price, RarityTag, Screen, Tabs, WeaponImg } from '../ui/kit';
import { MasteryBadge } from '../ui/MasteryBadge';
import { PetPreview } from '../ui/PetPreview';
import { ScopeSwatch } from '../ui/ScopeSwatch';
import { TracerSwatch } from '../ui/TracerSwatch';

type Tab = 'primary' | 'backup' | 'melee' | 'heavy' | 'sniper' | 'gadgets' | 'pets';
const TAB_SLOT: Record<Exclude<Tab, 'gadgets' | 'pets'>, number> = { primary: 0, backup: 1, melee: 2, heavy: 3, sniper: 4 };
const SLOT_TAB: Tab[] = ['primary', 'backup', 'melee', 'heavy', 'sniper'];
const OPTIONAL_SLOTS = new Set([3, 4]);

function sortByUnlock<T extends { unlockLevel: number; price: number }>(list: T[]): T[] {
  return [...list].sort((a, b) => a.unlockLevel - b.unlockLevel || a.price - b.price);
}

type ItemState = 'equipped' | 'owned' | 'buy' | 'locked';

function weaponState(w: WeaponDef): ItemState {
  if (loadout.value.weapons[w.slot] === w.id) return 'equipped';
  if (ownsItem('weapon', w.id)) return 'owned';
  if (level.value < itemInfo('weapon', w.id).unlockLevel) return 'locked';
  return 'buy';
}

function gadgetState(g: GadgetDef): ItemState {
  if (loadout.value.gadget === g.id) return 'equipped';
  if (ownsItem('gadget', g.id)) return 'owned';
  if (level.value < itemInfo('gadget', g.id).unlockLevel) return 'locked';
  return 'buy';
}

function petState(p: PetDef): ItemState {
  if (loadout.value.pet === p.id) return 'equipped';
  if (ownsItem('pet', p.id)) return 'owned';
  if (level.value < itemInfo('pet', p.id).unlockLevel) return 'locked';
  return 'buy';
}

type GearType = 'weapon' | 'gadget' | 'pet';

function StateChip(props: { state: ItemState; type: GearType; id: string }) {
  const info = itemInfo(props.type, props.id);
  if (props.type === 'weapon' && weaponOff(props.id))
    return (
      <span class="arm-chip locked">
        <Ban size={13} strokeWidth={3} /> Off for now
      </span>
    );
  if (props.state === 'equipped')
    return (
      <span class="arm-chip equipped">
        <Check size={14} strokeWidth={3.5} /> Equipped
      </span>
    );
  if (props.state === 'owned') return <span class="arm-chip owned">Owned</span>;
  if (props.state === 'locked')
    return (
      <span class="arm-chip locked">
        <Lock size={13} strokeWidth={3} /> Lv {info.unlockLevel}
      </span>
    );
  return <Price price={info.price} currency={info.currency} class="arm-chip-price" />;
}

// Loadout strip -------------------------------------------------------------------------------------------------

function LoadoutStrip(props: { onPick: (tab: Tab, id?: string) => void; tab: Tab }) {
  const l = loadout.value;
  const g = GADGET_BY_ID[l.gadget];
  const pet = l.pet ? PET_BY_ID[l.pet] : undefined;
  const camos = cosmetics.value.camos ?? {};
  const compact = useCompact();
  return (
    <div class="loadout-strip">
      {SLOT_TAB.map((tab, slot) => {
        const id = l.weapons[slot];
        const w = id ? WEAPON_BY_ID[id] : undefined;
        return (
          <button key={tab} class={`loadout-slot ${props.tab === tab ? 'is-on' : ''} ${w ? `r-${w.rarity}` : 'is-empty'}`} onClick={() => props.onPick(tab, id || undefined)}>
            <span class="loadout-slot-label">{SLOT_NAMES[slot]}</span>
            {w ? <WeaponImg weaponId={w.id} camo={camos[w.id]} scope={cosmetics.value.scopes?.[w.id]} scale={2} class="loadout-slot-img" /> : <span class="loadout-slot-none">Empty</span>}
            <span class="loadout-slot-name">{w?.name ?? '—'}</span>
          </button>
        );
      })}
      <button class={`loadout-slot is-gadget ${props.tab === 'gadgets' ? 'is-on' : ''}`} onClick={() => props.onPick('gadgets', l.gadget)}>
        <span class="loadout-slot-label">Gadget</span>
        {g ? (
          <span class="gadget-icon sm" style={{ '--g': hex(g.color) } as Record<string, string>}>
            <Icon name={g.icon} size={20} />
          </span>
        ) : (
          <span class="loadout-slot-none">Empty</span>
        )}
        <span class="loadout-slot-name">{g?.name ?? '—'}</span>
      </button>
      <button class={`loadout-slot is-pet ${props.tab === 'pets' ? 'is-on' : ''} ${pet ? '' : 'is-empty'}`} onClick={() => props.onPick('pets', pet?.id)}>
        <span class="loadout-slot-label">Pet</span>
        {pet ? <PetPreview petId={pet.id} styleId={cosmetics.value.petStyle} size={compact ? 24 : 34} /> : <span class="loadout-slot-none">None</span>}
        <span class="loadout-slot-name">{pet?.name ?? '—'}</span>
      </button>
    </div>
  );
}

// Cards ----------------------------------------------------------------------------------------------------------

/** Highest mastery tier earned with a weapon by takedowns, regardless of which camo is equipped ('none' = no badge). */
function earnedTier(w: WeaponDef): MasteryLevel {
  return masteryTierFor(w, me.value?.stats.weaponKills?.[w.id] ?? 0);
}

function WeaponCard(props: { w: WeaponDef; selected: boolean; onClick: () => void }) {
  const w = props.w;
  const st = weaponState(w);
  const camo = cosmetics.value.camos?.[w.id];
  return (
    <button class={`arm-card r-${w.rarity} ${props.selected ? 'is-on' : ''} st-${st}`} onClick={props.onClick}>
      <MasteryBadge tier={earnedTier(w)} size={22} class="on-card" />
      <span class="arm-card-img">
        <WeaponImg weaponId={w.id} camo={camo} scope={cosmetics.value.scopes?.[w.id]} scale={3} />
      </span>
      <span class="arm-card-name">{w.name}</span>
      <StateChip state={st} type="weapon" id={w.id} />
    </button>
  );
}

function GadgetCard(props: { g: GadgetDef; selected: boolean; onClick: () => void }) {
  const g = props.g;
  const st = gadgetState(g);
  return (
    <button class={`arm-card is-gadget ${props.selected ? 'is-on' : ''} st-${st}`} onClick={props.onClick}>
      <span class="arm-card-img">
        <span class="gadget-icon" style={{ '--g': hex(g.color) } as Record<string, string>}>
          <Icon name={g.icon} size={34} />
        </span>
      </span>
      <span class="arm-card-name">{g.name}</span>
      <StateChip state={st} type="gadget" id={g.id} />
    </button>
  );
}

function PetCard(props: { p: PetDef; selected: boolean; onClick: () => void }) {
  const p = props.p;
  const st = petState(p);
  const role = PET_ROLE[p.role];
  const compact = useCompact();
  return (
    <button class={`arm-card is-pet r-${itemInfo('pet', p.id).rarity} ${props.selected ? 'is-on' : ''} st-${st}`} onClick={props.onClick}>
      <span class="arm-card-img">
        <PetPreview petId={p.id} size={compact ? 60 : 88} />
        <span class="pet-role-badge" title={role.label}>
          <Icon name={role.icon} size={13} strokeWidth={2.6} />
          {role.label}
        </span>
      </span>
      <span class="arm-card-name">{p.name}</span>
      <StateChip state={st} type="pet" id={p.id} />
    </button>
  );
}

// Detail panels -----------------------------------------------------------------------------------------------------

function StatRow(props: { label: string; value: number; text: string }) {
  return (
    <div class="stat-row">
      <span class="stat-label">{props.label}</span>
      <Bar value={props.value} max={1} tone="orange" class="stat-bar" />
      <span class="stat-text">{props.text}</span>
    </div>
  );
}

function damageText(w: WeaponDef): string {
  if (w.beam) return `${w.beam.dps}/s`;
  if (w.projectile?.explosion) return `${w.projectile.explosion.damage + w.projectile.impactDamage} blast`;
  if (w.projectile) return String(w.projectile.impactDamage);
  if (w.pellets) return `${w.damage}×${w.pellets}`;
  return String(w.damage);
}

function destructionText(v: number): string {
  if (v > 0.8) return 'Extreme';
  if (v > 0.55) return 'High';
  if (v > 0.3) return 'Medium';
  return 'Low';
}

/**
 * Tap on a scope, tracer or camo chip that is not yours yet: buy it right here (after a confirm) and put it on the weapon, or say
 * exactly what is missing (level, coins, or that it is earned another way) instead of doing nothing.
 */
/** Small price (or "Lv N") under a chip the player does not own yet. */
function PriceTag(props: { type: 'scope' | 'tracer' | 'camo'; id: string }) {
  const info = itemInfo(props.type, props.id);
  if (info.source !== 'shop') return <small class="chip-price is-earned">Earned</small>;
  if (level.value < info.unlockLevel) return <small class="chip-price is-level">Lv {info.unlockLevel}</small>;
  return (
    <small class={`chip-price ${info.currency}`}>
      {fmtNum(info.price)} {info.currency === 'gems' ? '💎' : '🪙'}
    </small>
  );
}

async function buyChip(type: 'scope' | 'tracer' | 'camo', id: string, then: () => Promise<unknown>): Promise<void> {
  const info = itemInfo(type, id);
  const name = info.name;
  if (isOffline.value) {
    toast("You're offline", { kind: 'warning', body: 'Connect to buy things.' });
    return;
  }
  if (info.source !== 'shop') {
    toast(`${name} is earned`, { kind: 'info', body: info.source === 'battlepass' ? 'It comes from the Battle Pass.' : 'You get it by playing, not from the Store.' });
    return;
  }
  if (level.value < info.unlockLevel) {
    toast(`${name} unlocks at level ${info.unlockLevel}`, { kind: 'warning', body: `You are level ${level.value}. Keep playing to level up.` });
    return;
  }
  const have = info.currency === 'gems' ? wallet.value.gems : wallet.value.coins;
  if (have < info.price) {
    toast(`Not enough ${info.currency}`, { kind: 'warning', body: `${name} costs ${fmtNum(info.price)} and you have ${fmtNum(have)}.` });
    return;
  }
  const ok = await confirmDialog({
    title: `Buy ${name}?`,
    body: (
      <span class="confirm-price">
        <Price price={info.price} currency={info.currency} />
      </span>
    ),
    confirm: 'Buy',
  });
  if (ok && (await buyItem(type, id, name))) await then();
}

function Mastery(props: { w: WeaponDef; owned: boolean }) {
  const w = props.w;
  const kills = me.value?.stats.weaponKills?.[w.id] ?? 0;
  const mastery = CAMOS.filter((c) => c.masteryKills);
  // Per-weapon tier targets (harder weapons need fewer takedowns): masteryKillsFor scales the flat base.
  const upcoming = nextMasteryTier(w, kills);
  const next = upcoming ? { name: CAMOS.find((c) => c.id === upcoming.tier)?.name ?? upcoming.tier, kills: upcoming.kills } : null;
  const current = cosmetics.value.camos?.[w.id] ?? 'none';
  const shopCamos = inventory.value.filter((i) => i.type === 'camo' && !mastery.some((m) => m.id === i.id) && i.id !== 'none').map((i) => i.id);
  const options = [...CAMOS.map((c) => c.id), ...shopCamos.filter((id) => !CAMOS.some((c) => c.id === id))];
  return (
    <div class="mastery">
      <div class="mastery-head">
        <b>Mastery</b>
        <span>
          {fmtNum(kills)} takedowns{next ? ` · ${next.name} at ${fmtNum(next.kills)}` : ' · Maxed!'}
        </span>
      </div>
      {next && <Bar value={kills} max={next.kills} tone="gem" class="mastery-bar" />}
      <div class="camo-row">
        {options.map((id) => {
          const def = CAMOS.find((c) => c.id === id);
          const need = def?.masteryKills ? masteryKillsFor(w, def.masteryKills) : 0;
          const unlocked = id === 'none' || (def?.masteryKills ? kills >= need : ownsItem('camo', id));
          const colors = def?.colors ?? [0x888888, 0x666666, 0xaaaaaa];
          const name = def?.name ?? itemInfo('camo', id).name;
          return (
            <button
              key={id}
              class={`camo ${current === id ? 'is-on' : ''} ${unlocked ? '' : 'is-locked'}`}
              title={unlocked ? name : def?.masteryKills ? `${name}: ${fmtNum(need)} takedowns with this weapon` : `${name}: tap to buy`}
              disabled={unlocked && !props.owned}
              onClick={() => {
                if (unlocked) void setCamo(w.id, id);
                else if (def?.masteryKills) toast(`${name} mastery`, { kind: 'info', body: `Get ${fmtNum(need)} takedowns with the ${w.name} to unlock it (you have ${fmtNum(kills)}).` });
                else void buyChip('camo', id, () => setCamo(w.id, id));
              }}
            >
              <span
                class="camo-swatch"
                style={{ background: id === 'none' ? undefined : `linear-gradient(135deg, ${hex(colors[0])} 0 45%, ${hex(colors[1])} 45% 70%, ${hex(colors[2])} 70%)` }}
              />
              {!unlocked && <Lock size={12} class="camo-lock" />}
              <small>{name}</small>
              {!unlocked && !def?.masteryKills && <PriceTag type="camo" id={id} />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Bullet tracer picker for a hitscan weapon (cosmetic only). Mirrors the camo row: locked styles come from the Store. */
function TracerPicker(props: { w: WeaponDef; owned: boolean }) {
  const w = props.w;
  const current = cosmetics.value.tracers?.[w.id] ?? 'none';
  // Styles the player can see: everything on sale or owned (earned-only styles stay hidden until owned).
  const options = TRACERS.filter((t) => t.id === 'none' || ownsItem('tracer', t.id) || itemInfo('tracer', t.id).source === 'shop');
  return (
    <div class="mastery tracer-row">
      <div class="tracer-head">
        <b>Tracer</b>
        <span>How your shots look</span>
      </div>
      <div class="camo-row">
        {options.map((t) => {
          const unlocked = t.id === 'none' || ownsItem('tracer', t.id);
          return (
            <button
              key={t.id}
              class={`camo ${current === t.id ? 'is-on' : ''} ${unlocked ? '' : 'is-locked'}`}
              title={unlocked ? t.name : `${t.name}: tap to buy`}
              disabled={unlocked && !props.owned}
              onClick={() => (unlocked ? void setTracer(w.id, t.id) : void buyChip('tracer', t.id, () => setTracer(w.id, t.id)))}
            >
              <TracerSwatch id={t.id} fallback={w.tracer} />
              {!unlocked && <Lock size={12} class="camo-lock" />}
              <small>{t.name}</small>
              {!unlocked && <PriceTag type="tracer" id={t.id} />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Sight picker for weapons that can aim. Scopes change zoom and aiming speed (never damage); they are bought with coins in the Store. */
function ScopePicker(props: { w: WeaponDef; owned: boolean }) {
  const w = props.w;
  const options = scopesFor(w).filter((s) => s.id === 'none' || ownsItem('scope', s.id) || itemInfo('scope', s.id).source === 'shop');
  if (options.length < 2) return null;
  const current = cosmetics.value.scopes?.[w.id] ?? 'none';
  const cur = sightFor(w, current);
  const stock = sightFor(w, 'none');
  const speedPct = (v: number): number => Math.round(v * 100);
  return (
    <div class="mastery tracer-row scope-row">
      <div class="tracer-head">
        <b>Sight</b>
        <span>
          {zoomLabel(cur.zoom)} zoom · {speedPct(cur.adsSpeed)}% speed while aiming{cur.scopeId !== 'none' && stock.adsSpeed !== cur.adsSpeed ? ` (was ${speedPct(stock.adsSpeed)}%)` : ''}
        </span>
      </div>
      <div class="camo-row">
        {options.map((s) => {
          const unlocked = s.id === 'none' || ownsItem('scope', s.id);
          return (
            <button
              key={s.id}
              class={`camo ${current === s.id ? 'is-on' : ''} ${unlocked ? '' : 'is-locked'}`}
              title={unlocked ? `${s.name}: ${s.blurb}` : `${s.name}: ${s.blurb} Tap to buy.`}
              disabled={unlocked && !props.owned}
              onClick={() => (unlocked ? void setScope(w.id, s.id) : void buyChip('scope', s.id, () => setScope(w.id, s.id)))}
            >
              <ScopeSwatch id={s.id} />
              {!unlocked && <Lock size={12} class="camo-lock" />}
              <small>{s.name}</small>
              {!unlocked && <PriceTag type="scope" id={s.id} />}
            </button>
          );
        })}
      </div>
      <small class="scope-note">{(SCOPE_BY_ID_LOCAL[current] ?? SCOPE_BY_ID_LOCAL.none).blurb}</small>
    </div>
  );
}

function BuyOrEquip(props: { type: GearType; id: string; name: string; state: ItemState; onEquip: () => Promise<unknown>; onUnequip?: () => Promise<unknown> }) {
  const info = itemInfo(props.type, props.id);
  const w = wallet.value;
  const have = info.currency === 'gems' ? w.gems : w.coins;
  if (props.state === 'buy' && props.type === 'weapon' && weaponOff(props.id)) {
    return (
      <button class="btn big arm-action" disabled>
        <Ban size={20} /> Not for sale right now
      </button>
    );
  }
  if (props.state === 'locked') {
    return (
      <button class="btn big arm-action" disabled>
        <Lock size={20} /> Unlocks at level {info.unlockLevel}
      </button>
    );
  }
  if (props.state === 'buy') {
    const short = info.price - have;
    return (
      <AsyncButton
        class="primary big arm-action"
        disabled={short > 0 || isOffline.value}
        onClick={async () => {
          const ok = await confirmDialog({
            title: `Buy ${props.name}?`,
            body: (
              <span class="confirm-price">
                <Price price={info.price} currency={info.currency} />
              </span>
            ),
            confirm: 'Buy',
          });
          if (ok && (await buyItem(props.type, props.id, props.name))) await props.onEquip();
        }}
      >
        {isOffline.value ? 'Offline' : short > 0 ? `Need ${fmtNum(short)} more` : 'Buy'} <Price price={info.price} currency={info.currency} />
      </AsyncButton>
    );
  }
  if (props.state === 'owned') {
    return (
      <AsyncButton class="green big arm-action" onClick={props.onEquip}>
        Equip
      </AsyncButton>
    );
  }
  return (
    <div class="arm-action-row">
      <button class="btn big arm-action" disabled>
        <Check size={20} strokeWidth={3} /> Equipped
      </button>
      {props.onUnequip && (
        <AsyncButton class="arm-unequip" onClick={props.onUnequip}>
          Unequip
        </AsyncButton>
      )}
    </div>
  );
}

function WeaponDetail(props: { w: WeaponDef }) {
  const w = props.w;
  const st = weaponState(w);
  const { bars } = weaponStats(w);
  const camo = cosmetics.value.camos?.[w.id];
  const owned = st === 'owned' || st === 'equipped';
  return (
    <div class={`arm-detail r-${w.rarity}`}>
      <div class="arm-preview">
        <WeaponPreview weaponId={w.id} camo={camo} scope={cosmetics.value.scopes?.[w.id]} className="arm-preview-3d" />
      </div>
      <div class="arm-detail-head">
        <h2>{w.name}</h2>
        <RarityTag rarity={w.rarity} />
        <MasteryBadge tier={earnedTier(w)} size={26} label />
      </div>
      <p class="arm-desc">{w.description}</p>
      {weaponOff(w.id) && (
        <p class="arm-off">
          <Ban size={16} /> Switched off for now. In matches you'll use the {liveWeapons.value.fallback(w.slot)?.name ?? 'next weapon'} instead.
        </p>
      )}
      <div class="stat-list">
        <StatRow label={w.beam ? 'DPS' : 'Damage'} value={w.beam ? bars.dps : bars.damage} text={damageText(w)} />
        <StatRow label="Fire rate" value={bars.fireRate} text={w.burst ? `${w.burst}-burst` : w.melee ? `${w.rpm}/min` : `${w.rpm} rpm`} />
        <StatRow label="Range" value={bars.range} text={`${Math.round(w.melee?.range ?? w.beam?.range ?? w.range)} m`} />
        <StatRow label="Mobility" value={bars.mobility} text={`${Math.round(w.moveSpeed * 100)}%`} />
        <StatRow label="Destruction" value={bars.destruction} text={destructionText(bars.destruction)} />
      </div>
      {!w.melee && w.mag > 0 && (
        <div class="arm-facts">
          <span>Mag {w.mag}</span>
          <span>Reload {w.reload}s</span>
          {w.scope && <span>Scope</span>}
          {w.charge && <span>Charge {w.charge}s</span>}
          {w.penetration ? <span>Pierces walls</span> : null}
        </div>
      )}
      <Mastery w={w} owned={owned} />
      {w.kind === 'hitscan' && <TracerPicker w={w} owned={owned} />}
      <ScopePicker w={w} owned={owned} />
      <BuyOrEquip
        type="weapon"
        id={w.id}
        name={w.name}
        state={st}
        onEquip={() => equipWeapon(w.slot, w.id)}
        onUnequip={OPTIONAL_SLOTS.has(w.slot) ? () => equipWeapon(w.slot, '') : undefined}
      />
    </div>
  );
}

function GadgetDetail(props: { g: GadgetDef }) {
  const g = props.g;
  const st = gadgetState(g);
  const ex = g.projectile?.explosion;
  return (
    <div class="arm-detail is-gadget">
      <div class="arm-preview is-gadget">
        <span class="gadget-icon xl" style={{ '--g': hex(g.color) } as Record<string, string>}>
          <Icon name={g.icon} size={64} />
        </span>
      </div>
      <div class="arm-detail-head">
        <h2>{g.name}</h2>
      </div>
      <p class="arm-desc">{g.description}</p>
      <div class="arm-facts big">
        <span>
          <Timer size={16} /> {g.cooldown}s cooldown
        </span>
        {ex && <span>{ex.damage} blast damage</span>}
        {g.projectile?.sticky && <span>Sticky</span>}
        {g.duration ? <span>{g.duration}s duration</span> : null}
      </div>
      <BuyOrEquip type="gadget" id={g.id} name={g.name} state={st} onEquip={() => equipGadget(g.id)} />
    </div>
  );
}

/** Listed modes where pets stay home (MODE_INFO.pets, which the simulation also reads). */
const PETLESS_MODES = Object.values(MODE_INFO)
  .filter((m) => m.listed && m.pets === false)
  .map((m) => m.name);

function PetDetail(props: { p: PetDef }) {
  const p = props.p;
  const st = petState(p);
  const info = itemInfo('pet', p.id);
  const role = PET_ROLE[p.role];
  const owned = st === 'owned' || st === 'equipped';
  const compact = useCompact();
  return (
    <div class={`arm-detail is-pet r-${info.rarity}`}>
      <div class="arm-preview is-pet">
        <PetPreview petId={p.id} styleId={owned ? cosmetics.value.petStyle : undefined} size={compact ? 100 : 150} />
      </div>
      <div class="arm-detail-head">
        <h2>{p.name}</h2>
        <RarityTag rarity={info.rarity} />
        <span class="pet-role">
          <Icon name={role.icon} size={15} strokeWidth={2.6} /> {role.label}
        </span>
      </div>
      <p class="arm-desc">{p.description}</p>
      <div class="arm-facts big">
        {petFacts(p).map((f) => (
          <span key={f}>{f}</span>
        ))}
      </div>
      <BuyOrEquip type="pet" id={p.id} name={p.name} state={st} onEquip={() => equipPet(p.id)} onUnequip={() => equipPet('')} />
      {owned && (
        <button class="btn arm-styles" onClick={() => go('wardrobe', { tab: 'pets' })}>
          <Palette size={18} /> Pet styles
        </button>
      )}
    </div>
  );
}

// Screen --------------------------------------------------------------------------------------------------------------

export function ArmoryScreen() {
  const tab = (route.value.params.tab as Tab) || 'primary';
  const [selected, setSelected] = useState<string | null>(route.value.params.item ?? null);
  const isGadgets = tab === 'gadgets';
  const isPets = tab === 'pets';
  const slot = isGadgets || isPets ? -1 : TAB_SLOT[tab];
  const weapons = slot < 0 ? [] : sortByUnlock(SHOP_WEAPONS.filter((w) => w.slot === slot));
  const gadgets = sortByUnlock(GADGETS);
  const pets = sortByUnlock(PETS);
  useEffect(() => {
    // Default selection: the equipped item for this tab.
    if (isGadgets) setSelected((s) => (s && GADGET_BY_ID[s] ? s : loadout.value.gadget || gadgets[0]?.id || null));
    else if (isPets) setSelected((s) => (s && PET_BY_ID[s] ? s : loadout.value.pet || pets[0]?.id || null));
    else setSelected((s) => (s && WEAPON_BY_ID[s]?.slot === slot ? s : loadout.value.weapons[slot] || weapons[0]?.id || null));
  }, [tab]);
  const pick = (t: Tab, id?: string): void => {
    replace('armory', { tab: t });
    if (id) setSelected(id);
  };
  // Stats shown with the live balance applied.
  const selW = slot >= 0 && selected ? liveWeapons.value.byId[selected] : undefined;
  const selG = isGadgets && selected ? GADGET_BY_ID[selected] : undefined;
  const selP = isPets && selected ? PET_BY_ID[selected] : undefined;
  return (
    <Screen
      title="Armory"
      icon={<Crosshair size={26} />}
      class="armory"
      tabs={
        <Tabs<Tab>
          value={tab}
          onChange={(t) => pick(t)}
          tabs={[
            { id: 'primary', label: 'Primary' },
            { id: 'backup', label: 'Backup' },
            { id: 'melee', label: 'Melee' },
            { id: 'heavy', label: 'Heavy' },
            { id: 'sniper', label: 'Sniper' },
            { id: 'gadgets', label: 'Gadgets' },
            { id: 'pets', label: 'Pets' },
          ]}
        />
      }
    >
      <div class="split">
        <div class="split-main scr-scroll">
          <LoadoutStrip tab={tab} onPick={pick} />
          <div class="arm-grid" key={tab}>
            {isGadgets
              ? gadgets.map((g) => <GadgetCard key={g.id} g={g} selected={g.id === selected} onClick={() => setSelected(g.id)} />)
              : isPets
                ? pets.map((p) => <PetCard key={p.id} p={p} selected={p.id === selected} onClick={() => setSelected(p.id)} />)
                : weapons.map((w) => <WeaponCard key={w.id} w={w} selected={w.id === selected} onClick={() => setSelected(w.id)} />)}
          </div>
          {isPets && (
            <p class="arm-hint">
              Pets follow you into battle and can be shot apart, then rebuild. They stay home in {PETLESS_MODES.slice(0, -1).join(', ')} and {PETLESS_MODES[PETLESS_MODES.length - 1]}.
            </p>
          )}
        </div>
        <div class="split-side scr-scroll">{selW ? <WeaponDetail w={selW} /> : selG ? <GadgetDetail g={selG} /> : selP ? <PetDetail p={selP} /> : null}</div>
      </div>
    </Screen>
  );
}
